import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// Depreciation Calculator — supports Straight Line, Declining Balance, Double Declining, Sum-of-Years-Digits
export default function DepreciationCalc() {
  const [method, setMethod] = useState('straight');
  const [cost, setCost] = useState(11000);
  const [salvage, setSalvage] = useState(1000);
  const [years, setYears] = useState(5);
  const [decliningFactor, setDecliningFactor] = useState(2);
  const [round, setRound] = useState('yes');
  const [partial, setPartial] = useState('no');

  const c = useMemo(() => {
    const rows = [];
    const depBase = cost - salvage;
    if (method === 'straight') {
      const annual = depBase / years;
      let bal = cost;
      for (let y = 1; y <= years; y++) {
        bal -= annual;
        rows.push({ year: y, depreciation: annual, endBook: bal });
      }
    } else if (method === 'declining' || method === 'ddb') {
      const factor = method === 'ddb' ? 2 : decliningFactor;
      const rate = factor / years;
      let bal = cost;
      for (let y = 1; y <= years; y++) {
        let dep = bal * rate;
        if (bal - dep < salvage) dep = bal - salvage;
        bal -= dep;
        rows.push({ year: y, depreciation: dep, endBook: bal });
      }
    } else if (method === 'sum') {
      const sum = years * (years + 1) / 2;
      let bal = cost;
      for (let y = 1; y <= years; y++) {
        const factor = (years - y + 1) / sum;
        const dep = depBase * factor;
        bal -= dep;
        rows.push({ year: y, depreciation: dep, endBook: bal });
      }
    }
    if (round === 'yes') rows.forEach(r => { r.depreciation = Math.round(r.depreciation); r.endBook = Math.round(r.endBook); });
    return { rows };
  }, [method, cost, salvage, years, decliningFactor, round, partial]);

  return (
    <CalcShell look="cnet" title="Depreciation Calculator" subtitle="Compute depreciation using Straight Line, Declining Balance, Double Declining Balance, or Sum-of-Years-Digits."
      about={<><p><b>Depreciation</b> spreads the cost of a tangible asset over its useful life. Different methods yield different year-by-year expense patterns — with declining and sum-of-years-digits front-loading the depreciation.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Depreciation method</label>
            <select value={method} onChange={e=>setMethod(e.target.value)}>
              <option value="straight">Straight Line</option>
              <option value="declining">Declining Balance</option>
              <option value="ddb">Double Declining Balance</option>
              <option value="sum">Sum of the Year's Digits</option>
            </select>
          </div>
          {method === 'declining' && <div className="field"><label>Depreciation factor</label><input type="number" step="0.1" value={decliningFactor} onChange={e=>setDecliningFactor(+e.target.value||0)}/></div>}
          <div className="field"><label>Asset cost</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={cost} onChange={e=>setCost(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Salvage value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={salvage} onChange={e=>setSalvage(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Depreciation years</label><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/></div>
          <div className="field"><label>Round to dollars?</label>
            <div className="flex gap-4 mt-1"><label><input type="radio" checked={round==='yes'} onChange={()=>setRound('yes')}/> Yes</label><label><input type="radio" checked={round==='no'} onChange={()=>setRound('no')}/> No</label></div>
          </div>
          <div className="field"><label>Partial year depreciation?</label>
            <div className="flex gap-4 mt-1"><label><input type="radio" checked={partial==='yes'} onChange={()=>setPartial('yes')}/> Yes</label><label><input type="radio" checked={partial==='no'} onChange={()=>setPartial('no')}/> No</label></div>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Depreciation Schedule</h3>
          <table className="w-full text-sm border rounded-lg overflow-hidden">
            <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Depreciation</th><th className="px-3 py-2 text-right">End Book Value</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right text-orange-600" data-testid={i===0?'dep-year1':undefined}>{fmt(r.depreciation)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.endBook)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Depreciation Bar Chart</h3>
        <BarChart
          data={c.rows.map(r => ({
            label: `Y${r.year}`,
            values: [
              { value: r.depreciation, color: '#f97316' },
              { value: r.endBook,      color: '#2563eb' },
            ]
          }))}
          stack={false}
          legend={[
            { label: 'Depreciation for year', color: '#f97316' },
            { label: 'End book value',        color: '#2563eb' },
          ]}
        />
      </div>
    </CalcShell>
  );
}
